import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import StatCard from '../insights/StatCard'
import { GroupedAvatars, IMembers } from '../Avatar'
import Text from '../Text'

// Types
import { RevealReasonType } from '../../common/models/Evaluation'
import { ICompanyMember } from '../../common/models/Company'

// Maps a gate reason to the quiet placeholder copy shown in the pending state.
// notAssessed has no card (hasResults gates it out) so it needs no entry.
const PENDING_COPY_KEY: Partial<Record<RevealReasonType, string>> = {
  [RevealReasonType.hiddenUntilSelfAssess]:
    'userProfileTermReviews.pendingSelfAssess',
  [RevealReasonType.hiddenUntilComplete]:
    'userProfileTermReviews.pendingComplete',
  [RevealReasonType.hiddenManual]: 'userProfileTermReviews.pendingManual',
  [RevealReasonType.notSubmitted]: 'userProfileTermReviews.pendingNotSubmitted',
}

type SectionFilter = 'all' | 'skills' | 'culture' | 'goals'

// 'revealed' shows the full stat. 'pending' replaces value/delta/bar with a
// quiet, same-footprint placeholder. 'empty' is a quiet "no results this round"
// placeholder. 'noScore' keeps the card header + avatar strip but swaps the
// value/bar for a short "no overall score" label — used when submissions exist
// but no aggregate score was produced, so the card stays clickable.
export type StatCardRevealState = 'revealed' | 'pending' | 'empty' | 'noScore'

// A single assessor row under a stat card: the member and whether they have
// submitted their part this term.
export interface IStatAssessor {
  member: ICompanyMember
  submitted: boolean
}

// The data backing one stat card (skills/culture/goals) in the member views.
export interface IStatCardData {
  value?: number
  previous?: number
  subtitle?: string
  // Segmented bar (skills/culture). Omit for the continuous goals bar.
  segments?: { total: number; complete: number }
  assessors: IStatAssessor[]
  // Hide the card entirely when the section has no results this term.
  hasResults?: boolean
  // Visual reveal mode; defaults to 'revealed'.
  revealState?: StatCardRevealState
  // Drives the quiet pending placeholder copy (see PENDING_COPY_KEY).
  revealReason?: RevealReasonType
}

interface IProps {
  skill: IStatCardData
  culture: IStatCardData
  goals: IStatCardData
  compareActive?: boolean
  // When filtered to a single section, only that section's card is shown.
  sectionFilter?: SectionFilter
  // When provided, each card becomes clickable and drills into its detail view.
  onCardClick?: (section: 'skills' | 'culture' | 'goals') => void
  // Assessed member's name, woven into the self-assess pending copy.
  memberName?: string
  // Max avatars in the assessor strip before they collapse into a "+N" circle.
  // Defaults to GroupedAvatars' own 3; the collection view passes a higher value.
  assessorMaxLength?: number
  className?: string
}

// Per-member stat blocks: Skills + Culture average-score cards side by side,
// with a full-width Goals achievement-rate card below. Purely presentational —
// the parent computes every value.
const MemberStatBlocks: React.FC<IProps> = ({
  skill,
  culture,
  goals,
  compareActive,
  sectionFilter = 'all',
  onCardClick,
  memberName,
  assessorMaxLength,
  className,
}) => {
  const { t } = useTranslation()

  // A card shows when its section is in the active filter AND either the caller
  // supplied a revealState for it (the collection page always does — it renders
  // all three sections, using the 'empty' state for sections with no results)
  // or the section has results this term. Without a per-section revealState we
  // keep today's hasResults gating (the manage usage), so an empty "0 of 0"
  // card never renders there.
  const inFilter = (section: SectionFilter) =>
    sectionFilter === 'all' || sectionFilter === section
  const shows = (data: IStatCardData) =>
    data.revealState !== undefined || data.hasResults !== false
  const showSkill = inFilter('skills') && shows(skill)
  const showCulture = inFilter('culture') && shows(culture)
  const showGoals = inFilter('goals') && shows(goals)

  const pendingText = (data: IStatCardData) => {
    const key = data.revealReason && PENDING_COPY_KEY[data.revealReason]
    if (!key) return undefined
    // Only the self-assess copy names the assessed member. Fall back to the
    // member's name as the {{name}} value when none is supplied so the copy
    // never renders a raw placeholder.
    const isSelfAssess =
      data.revealReason === RevealReasonType.hiddenUntilSelfAssess
    return isSelfAssess
      ? t(key, { name: memberName || t('feedbackForm.thisMember') })
      : t(key)
  }

  // Here 'empty' only ever means notAssessed (no assessors configured) — the
  // "submitted but no aggregate score" case is the openable 'noScore' card.
  const emptyText = t('userProfileTermReviews.emptyNotAssessed')
  const noScoreText = t('userProfileTermReviews.noOverallScore')
  const noScoreHint = t('userProfileTermReviews.noOverallScoreHint')

  // Cards with something to drill into: revealed (a score) and noScore
  // (individual submissions behind it). Pending and empty cards have nothing to
  // show, so they get no click handler (and no chevron).
  const isClickable = (data: IStatCardData) =>
    data.revealState === undefined ||
    data.revealState === 'revealed' ||
    data.revealState === 'noScore'

  const renderAvatars = (assessors: IStatAssessor[]) =>
    assessors.length > 0 && (
      <>
        <StyledGroupedAvatars
          maxLength={assessorMaxLength}
          members={assessors.map<IMembers>(({ member, submitted }) => ({
            name: member.name,
            avatar: member.picture,
            pending: !submitted,
            statusLabel: submitted
              ? t('userProfileTermReviews.statusSubmitted')
              : t('userProfileTermReviews.statusPending'),
          }))}
        />
        <Text textStyle="caption" color="textLight" tag="span">
          {t('userProfileTermReviews.assessorCount', {
            count: assessors.length,
          })}
        </Text>
      </>
    )

  const skillCard = (
    <StatCard
      framed
      icon="Skills"
      titleKey="general.skills"
      value={skill.value}
      showScale
      subtitle={skill.subtitle}
      segments={skill.segments}
      current={skill.value}
      previous={skill.previous}
      compareActive={compareActive}
      revealState={skill.revealState}
      pendingText={pendingText(skill)}
      emptyText={emptyText}
      noScoreText={noScoreText}
      noScoreHint={noScoreHint}
      onClick={
        onCardClick && isClickable(skill)
          ? () => onCardClick('skills')
          : undefined
      }
    >
      {renderAvatars(skill.assessors)}
    </StatCard>
  )

  const cultureCard = (
    <StatCard
      framed
      icon="Culture"
      titleKey="general.feedback.culture"
      value={culture.value}
      showScale
      subtitle={culture.subtitle}
      segments={culture.segments}
      current={culture.value}
      previous={culture.previous}
      compareActive={compareActive}
      revealState={culture.revealState}
      pendingText={pendingText(culture)}
      emptyText={emptyText}
      noScoreText={noScoreText}
      noScoreHint={noScoreHint}
      onClick={
        onCardClick && isClickable(culture)
          ? () => onCardClick('culture')
          : undefined
      }
    >
      {renderAvatars(culture.assessors)}
    </StatCard>
  )

  const goalsCard = (
    <StatCard
      framed
      icon="Goals"
      titleKey="general.goals.goals"
      value={goals.value}
      suffix="%"
      subtitle={goals.subtitle}
      current={goals.value}
      previous={goals.previous}
      compareActive={compareActive}
      revealState={goals.revealState}
      pendingText={pendingText(goals)}
      emptyText={emptyText}
      noScoreText={noScoreText}
      noScoreHint={noScoreHint}
      onClick={
        onCardClick && isClickable(goals)
          ? () => onCardClick('goals')
          : undefined
      }
    >
      {renderAvatars(goals.assessors)}
    </StatCard>
  )

  return (
    <Wrapper className={className}>
      {showSkill && showCulture ? (
        <ScoreRow>
          {skillCard}
          {cultureCard}
        </ScoreRow>
      ) : (
        <>
          {showSkill && skillCard}
          {showCulture && cultureCard}
        </>
      )}
      {showGoals && goalsCard}
    </Wrapper>
  )
}

export default MemberStatBlocks

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ScoreRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const StyledGroupedAvatars = styled(GroupedAvatars)`
  display: inline-flex;
  margin-left: 0;
  background-color: unset;
  box-shadow: none;
`
